import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import {
  dateTimeFormatted,
  formatAppointmentDateTime,
  formattedDate,
} from "../utils/dateUtils";
import { preventDuplicateRequest } from "../utils/requestManager";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

export interface AssignedUser {
  userId: number;
  username: string;
  fullname: string;
  emailId: string;
  departmentName: string | null;
  genderName: string | null;
  phoneNumber: string | null;
  userTypeName: string;
  userRoleName: string;
  teamName: string | null;
}

export interface UpdateAssignedToRequest {
  subjectQMEScheduleIds: string;
  rorAssignedUserID: number;
}

const recordReviewer = process.env.REACT_APP_CASE_URL || "";
if (!recordReviewer) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables."
  );
}

const RecordReviewerAPI = api(recordReviewer);

const getRecordReviewerCardSearch = async (paramData: any): Promise<any> => {
  const key = `getRecordReviewerCardSearch-${JSON.stringify(paramData)}`;
  return preventDuplicateRequest(key, async () => {
    try {
      const response = await RecordReviewerAPI.post<ApiResponse<any>>(
        "/api/reviewer/summary",
        paramData
      );

      return response.data?.result;
    } catch (error) {
      console.error("Error while getting data:", error);
      throw new Error(
        "Failed to getting data. Please check the data and try again."
      );
    }
  });
};

const getRecordReviewerSimpleSearch = async (paramData: any): Promise<any> => {
  const key = `getRecordReviewerSimpleSearch-${JSON.stringify(paramData)}`;
  return preventDuplicateRequest(key, async () => {
    try {
      const response = await RecordReviewerAPI.post<ApiResponse<any>>(
        "/api/reviewer/schedule",
        paramData
      );
      const updatedResult = response.data?.result.map((data: any) => ({
        ...data,
        record_Received: formatAppointmentDateTime(data.recordReceivedDate),
        appointment_Date: formatAppointmentDateTime(data.appointmentDate),
        due_Date: formattedDate(data.rorDueDateDesc),
        ror_UploadedDate: formattedDate(data.rorUploadedDate),
      }));

      return updatedResult;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again."
      );
    }
  });
};

const GetRecordReviewerAssignedTo = async (): Promise<
  ApiResponse<AssignedUser[]> | undefined
> => {
  try {
    const response = await RecordReviewerAPI.get(`/api/reviewer/assignee/list`);
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const GetAssignedToReviewerList = async (
  loggedInUserId: Number
): Promise<ApiResponse<AssignedUser[]> | undefined> => {
  try {
    const response = await RecordReviewerAPI.get(
      `/api/reviewer/assignee/list?`
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const updateRecordReviewerAssignedTo = async (
  data: UpdateAssignedToRequest
): Promise<ApiResponse<any>> => {
  try {
    const response = await RecordReviewerAPI.put<ApiResponse<any>>(
      "/api/reviewer/details/update",
      data
    );
    return response.data;
  } catch (error) {
    throw new Error("Unable to update assigned To. Please try again later.");
  }
};

const GetReviewerRecordSummaryList = async (
  caseInfoId: Number,
  qmeScheduleId: Number,
  searchString: string = ""
): Promise<ApiResponse<any[]> | undefined> => {
  try {
    const response = await RecordReviewerAPI.get(
      `/api/reviewer/record-summary/list?caseInfoId=${caseInfoId}&qmeScheduleId=${qmeScheduleId}&searchString=${searchString}`
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const GetReviewerRecordList = async (
  caseInfoId: Number,
  senderKeyId: Number,
  qmeScheduleId: Number,
  searchString: string = ""
): Promise<ApiResponse<any[]> | undefined> => {
  try {
    const response = await RecordReviewerAPI.get(
      `/api/reviewer/record/list?caseInfoId=${caseInfoId}&senderKeyId=${senderKeyId}&qmeScheduleId=${qmeScheduleId}&searchString=${searchString}`
    );

    let counter = 0;
    return {
      ...response.data,
      result:
        response.data.result?.map((item: any) => {
          let latestPosition: string | number = "";

          if (item?.documentTypeName === "Preliminary Record Review Summary") {
            counter += 1;
            latestPosition = counter;
          }
          return {
            ...item,
            id: item.caseRecordDetailsID,
            uploadDateTimeNew: dateTimeFormatted(item.uploadDateTime),
            pagesReviewed: item.pageReviewed,
            downloadROR: item.cloudLocation,
            fileAction: item.noOfPages,
            latestPosition,
          };
        }) || [],
    };
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const GetReviewerRORStatusUpdateList = async (
  subjectQmeScheduleId: Number
): Promise<ApiResponse<any[]> | undefined> => {
  try {
    const response = await RecordReviewerAPI.get(
      `/api/reviewer/ror-status-updates/list?subjectQmeScheduleId=${subjectQmeScheduleId}`
    );

    return {
      ...response.data,
      result:
        response.data.result?.map((item: any) => ({
          ...item,
          createdDateNew: dateTimeFormatted(item.createdDate),
        })) || [],
    };
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const GetReviewerRORWorkingLogsList = async (
  subjectQmeScheduleId: Number
): Promise<ApiResponse<any[]> | undefined> => {
  try {
    const response = await RecordReviewerAPI.get(
      `/api/reviewer/ror-worklogs/list?subjectQmeScheduleId=${subjectQmeScheduleId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const getRORRecordsEmail = async (subjectQMEScheduleId: any): Promise<any> => {
  try {
    const response = await RecordReviewerAPI.get<ApiResponse<any>>(
      `/api/reviewer/email/final-document-upload?subjectQmeScheduleId=${subjectQMEScheduleId}`
    );

    return response;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again."
    );
  }
};

const getRORExceptionEmail = async (
  subjectQMEScheduleId: any
): Promise<any> => {
  try {
    const response = await RecordReviewerAPI.get<ApiResponse<any>>(
      `/api/reviewer/email/ror/exception?subjectQmeScheduleId=${subjectQMEScheduleId}`
    );

    return response;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again."
    );
  }
};

const getRORNewPagesEmail = async (subjectQMEScheduleId: any): Promise<any> => {
  try {
    const response = await RecordReviewerAPI.get<ApiResponse<any>>(
      `/api/reviewer/email/ror/new-pages-for-review?subjectQmeScheduleId=${subjectQMEScheduleId}`
    );

    return response;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again."
    );
  }
};

export {
  getRecordReviewerCardSearch,
  getRecordReviewerSimpleSearch,
  GetRecordReviewerAssignedTo,
  updateRecordReviewerAssignedTo,
  GetAssignedToReviewerList,
  GetReviewerRecordSummaryList,
  GetReviewerRecordList,
  GetReviewerRORStatusUpdateList,
  GetReviewerRORWorkingLogsList,
  getRORRecordsEmail,
  getRORExceptionEmail,
  getRORNewPagesEmail,
};
